import React, { useState, useEffect, useRef } from "react";
import "./index.scss";
import { Button, message, Spin } from "antd";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import WavvestTextLogo from "@/assets/logo/wavvest-text.svg";
import AprilLogo from "@/assets/logo/april.png";
import InfoCircleIcon from "@/assets/icons/info-circle.svg";
import ArrowRightIcon from "@/assets/icons/arrow-right.svg";
import CheckCircleIcon from "@/assets/icons/check-circle.svg";
import WarningCircleIcon from "@/assets/icons/warning-circle.svg";
import ChevronRightIcon from "@/assets/icons/chevron-right.svg";
import { openAprilInNewTab, type AprilApplication } from "@/config/april";
import {
  checkUserSignedUp,
  getUserAppState,
  getTaxReturnFiles,
  getUserReturnData,
} from "@/services/tax-service";
import type { TaxReturnFileDownloadLink } from "@/types/tax";

type TaxStep = "loading" | "empty" | "services";
type ServiceStatus = "not_started" | "in_progress" | "completed";
type ServiceType = "Annual" | "Ongoing";

interface TaxService {
  id: string;
  task: string;
  date: string | null;
  status: ServiceStatus;
  type: ServiceType;
  progress: number | null;
  aprilApplication: AprilApplication;
}

interface TaxReturnSummary {
  taxYear: string;
  federalRefund: number | null;
  stateRefund: number | null;
  totalRefund: number | null;
  // true if refund, false if owed, null if neutral/unknown (drives the header — based on total)
  isRefund: boolean | null;
  // Per-card refund/owed state, so federal and state can differ (e.g. federal refund, state owed)
  federalIsRefund: boolean | null;
  stateIsRefund: boolean | null;
  files: TaxReturnFileDownloadLink | null;
}

// A calculated tax liability, normalized for display.
// Convention (matches existing web behavior): negative liability == refund (overpaid),
// null/0 == neutral/unknown. Amount is always the absolute value.
const normalizeLiability = (
  liability: number | null,
): { amount: number | null; isRefund: boolean | null } => ({
  amount: liability !== null ? Math.abs(liability) : null,
  isRefund: liability === null || liability === 0 ? null : liability < 0,
});

// Card label that follows the card's own refund/owed state, so a label never
// reads "REFUND" over an owed amount. null (unknown or $0 balance) stays neutral.
const refundLabelFor = (
  prefix: "FEDERAL" | "STATE" | "TOTAL",
  cardIsRefund: boolean | null,
): string => {
  if (cardIsRefund === true) return `${prefix} REFUND`;
  if (cardIsRefund === false) return `${prefix} OWED`;
  return `${prefix} BALANCE`;
};

const initialServices: TaxService[] = [
  {
    id: "tax-filing",
    task: "Tax filing",
    date: null,
    status: "not_started",
    type: "Annual",
    progress: null,
    aprilApplication: "FILING",
  },
  {
    id: "paycheck-estimation",
    task: "Paycheck estimation",
    date: null,
    status: "not_started",
    type: "Ongoing",
    progress: null,
    aprilApplication: "PAYCHECK_ESTIMATION",
  },
  {
    id: "paycheck-optimization",
    task: "Paycheck optimization",
    date: null,
    status: "not_started",
    type: "Ongoing",
    progress: null,
    aprilApplication: "PAYCHECK_OPTIMIZATION",
  },
];

const getStatusLabel = (status: ServiceStatus): string => {
  switch (status) {
    case "not_started":
      return "Not Started";
    case "in_progress":
      return "In Progress";
    case "completed":
      return "Completed";
    default:
      return "Not Started";
  }
};

const getActionLabel = (status: ServiceStatus): string => {
  switch (status) {
    case "not_started":
      return "Start Now";
    case "in_progress":
      return "Continue";
    case "completed":
      return "Completed";
    default:
      return "Start Now";
  }
};

// Format currency
const formatCurrency = (amount: number | null): string => {
  if (amount === null) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
};

// Request the prior tax year: the current year isn't fileable in April until the
// following Jan–Apr, so asking for it off-season 500s. Matches the mobile app.
const getCurrentTaxYear = (): string => {
  return (new Date().getFullYear() - 1).toString();
};

const Tax: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState<TaxStep>("loading");
  const [services, setServices] = useState<TaxService[]>(initialServices);
  const [taxReturnSummary, setTaxReturnSummary] =
    useState<TaxReturnSummary | null>(null);
  const hasFetchedRef = useRef(false);
  const containerClass = `tax-container ${theme}-theme`;

  // Check April registration status and fetch tax status on component mount
  useEffect(() => {
    // Prevent duplicate calls (React StrictMode double-renders in dev)
    if (hasFetchedRef.current) {
      return;
    }

    const fetchTaxData = async () => {
      if (!user?.email) {
        setCurrentStep("empty");
        return;
      }

      hasFetchedRef.current = true;

      try {
        // First check if user is signed up with April
        const isSignedUp = await checkUserSignedUp(user.email);

        if (!isSignedUp) {
          setCurrentStep("empty");
          return;
        }

        // User is signed up, fetch status for each service
        const taxYear = getCurrentTaxYear();
        const updatedServices = await Promise.all(
          initialServices.map(async (service) => {
            try {
              const appState = await getUserAppState(
                user.email,
                service.aprilApplication,
                taxYear,
              );
              // Access userState from the response
              const userState = appState.userState;
              const progress = userState?.applicationProgress
                ? Number(userState.applicationProgress)
                : null;

              // Determine status based on progress and user state
              let status: ServiceStatus = "not_started";
              if (progress !== null && progress >= 100) {
                status = "completed";
              } else if (progress !== null && progress > 0) {
                status = "in_progress";
              }

              return {
                ...service,
                status,
                progress,
              };
            } catch (error) {
              console.error(
                `Failed to get state for ${service.aprilApplication}:`,
                error,
              );
              return service;
            }
          }),
        );

        setServices(updatedServices);

        // Try to fetch tax return files and return data if FILING service is completed
        const filingService = updatedServices.find(
          (s) => s.aprilApplication === "FILING",
        );
        if (filingService?.status === "completed") {
          try {
            // Fetch both files and return data in parallel
            const [filesResponse, returnDataResponse] = await Promise.all([
              getTaxReturnFiles(user.email, "FILING", taxYear),
              getUserReturnData(user.email, "FILING", taxYear),
            ]);

            // Calculate refund amounts from return data.
            // Prefer the per-return federal/state breakdown when the backend
            // provides it; otherwise fall back to the flat total liability so
            // behavior is unchanged for returns without the detailed objects.
            const returnData = returnDataResponse.userReturnData;
            // Base breakdown availability on the detailed liability *values*, not
            // just the presence of the detailed objects: an object can exist
            // while its currentlyCalculatedTaxLiability is absent, which would
            // otherwise suppress the total-liability fallback and render "--".
            const federalDetailLiability =
              returnData?.federalUserReturnDetailedInfo
                ?.currentlyCalculatedTaxLiability ?? null;
            const stateDetailLiability =
              returnData?.stateUserReturnDetailedInfo
                ?.currentlyCalculatedTaxLiability ?? null;
            const hasBreakdown =
              federalDetailLiability !== null || stateDetailLiability !== null;

            const federalLiability = hasBreakdown
              ? federalDetailLiability
              : (returnData?.currentlyCalculatedTotalTaxLiability ?? null);
            const stateLiability = hasBreakdown ? stateDetailLiability : null;
            const totalLiability =
              returnData?.currentlyCalculatedTotalTaxLiability ??
              (hasBreakdown &&
              (federalLiability !== null || stateLiability !== null)
                ? (federalLiability ?? 0) + (stateLiability ?? 0)
                : null);

            const federal = normalizeLiability(federalLiability);
            const state = normalizeLiability(stateLiability);
            const total = normalizeLiability(totalLiability);

            setTaxReturnSummary({
              taxYear,
              federalRefund: federal.amount,
              stateRefund: state.amount,
              totalRefund: total.amount,
              isRefund: total.isRefund,
              federalIsRefund: federal.isRefund,
              stateIsRefund: state.isRefund,
              files: filesResponse.files,
            });
          } catch (error) {
            console.error("Failed to fetch tax return data:", error);
            // Still show overview with null values if API fails
            setTaxReturnSummary({
              taxYear,
              federalRefund: null,
              stateRefund: null,
              totalRefund: null,
              isRefund: null,
              federalIsRefund: null,
              stateIsRefund: null,
              files: null,
            });
          }
        }

        setCurrentStep("services");
      } catch (error) {
        console.error("Failed to fetch tax data:", error);
        setCurrentStep("empty");
      }
    };

    fetchTaxData();
  }, [user?.email]);

  const handleGetStarted = () => {
    setCurrentStep("services");
  };

  const handleServiceClick = (service: TaxService) => {
    if (!user) {
      message.error("Please log in to access tax services");
      return;
    }

    const partnerUserId = user.userId;
    const email = user.email;

    if (!partnerUserId || !email) {
      message.error("Unable to get user information");
      return;
    }

    openAprilInNewTab(service.aprilApplication, partnerUserId, email);
  };

  const handleDownloadDocuments = () => {
    if (!taxReturnSummary?.files) return;

    const { files } = taxReturnSummary;

    // Download federal file if ready
    if (files.isFederalFileReady && files.federalFileLink) {
      window.open(files.federalFileLink, "_blank", "noopener,noreferrer");
    }

    // Download state file if ready
    if (files.isStateFileReady && files.stateFileLink) {
      window.open(files.stateFileLink, "_blank", "noopener,noreferrer");
    }
  };

  const renderLoadingState = () => (
    <div className="tax-loading">
      <Spin size="large" />
    </div>
  );

  const renderEmptyState = () => (
    <div className="tax-content-box">
      <div className="content-primary">
        <img src={InfoCircleIcon} alt="Info" className="info-icon" />
        <h2 className="title">Connect to April</h2>
        <p className="description">
          Streamline your tax filing process with our partnership with April.
        </p>
      </div>

      <div className="content-secondary">
        <p className="terms">
          By continuing, you agree to the{" "}
          <a href="#" className="terms-link">
            Wavvest Terms and Conditions
          </a>
          .
        </p>

        <Button
          type="primary"
          className="get-started-btn"
          onClick={handleGetStarted}
        >
          <span className="btn-text">Get Started</span>
          <img src={ArrowRightIcon} alt="Arrow" className="arrow-icon" />
        </Button>
      </div>
    </div>
  );

  const renderTaxReturnSummary = () => {
    if (!taxReturnSummary) return null;
    // Only show as owed when isRefund is false and we have known liability data
    const hasRefundData =
      taxReturnSummary.federalRefund !== null ||
      taxReturnSummary.stateRefund !== null;
    const isOwed = taxReturnSummary?.isRefund === false && hasRefundData;
    const isNeutral = taxReturnSummary.totalRefund === 0;
    const isUnknown = taxReturnSummary.totalRefund === null;
    // Per-card styling: a card reads "owed" only when its own figure is owed.
    const amountClassFor = (cardIsRefund: boolean | null): string =>
      cardIsRefund === false ? "refund-amount owed" : "refund-amount";
    const hasDownloadableFiles =
      taxReturnSummary.files?.isFederalFileReady ||
      taxReturnSummary.files?.isStateFileReady;

    return (
      <div className="tax-return-box">
        <div className="return-header">
          <div className="return-title-wrapper">
            {isOwed && (
              <img
                src={WarningCircleIcon}
                alt="Warning"
                className="warning-icon"
              />
            )}
            <div className="return-title-section">
              <h2 className="return-title">
                Your {taxReturnSummary.taxYear} Tax Return
              </h2>
              <p className="return-subtitle">
                {isOwed
                  ? "You owe taxes this year."
                  : isNeutral
                    ? "You have no balance due or refund."
                    : isUnknown
                      ? "Your tax outcome is not available yet."
                      : "You're getting a refund!"}
              </p>
            </div>
          </div>
          {hasDownloadableFiles && (
            <Button className="download-btn" onClick={handleDownloadDocuments}>
              Download Return Documents
            </Button>
          )}
        </div>

        <div className="refund-cards">
          <div className="refund-card">
            <span className="refund-label">
              {refundLabelFor("FEDERAL", taxReturnSummary.federalIsRefund)}
            </span>
            <span className={amountClassFor(taxReturnSummary.federalIsRefund)}>
              {formatCurrency(taxReturnSummary.federalRefund)}
            </span>
          </div>
          <div className="refund-card">
            <span className="refund-label">
              {refundLabelFor("STATE", taxReturnSummary.stateIsRefund)}
            </span>
            <span className={amountClassFor(taxReturnSummary.stateIsRefund)}>
              {formatCurrency(taxReturnSummary.stateRefund)}
            </span>
          </div>
          <div className="refund-card">
            <span className="refund-label">
              {refundLabelFor("TOTAL", taxReturnSummary.isRefund)}
            </span>
            <span className={`${amountClassFor(taxReturnSummary.isRefund)} total`}>
              {formatCurrency(taxReturnSummary.totalRefund)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderActionButton = (service: TaxService) => {
    const isCompleted = service.status === "completed";

    return (
      <button
        className={`action-btn action-${service.status}`}
        onClick={() => handleServiceClick(service)}
      >
        {getActionLabel(service.status)}{" "}
        {isCompleted ? (
          <img src={CheckCircleIcon} alt="Completed" className="action-check" />
        ) : (
          <img src={ArrowRightIcon} alt="Arrow" className="action-arrow" />
        )}
      </button>
    );
  };

  const renderProgressCell = (service: TaxService) => {
    if (service.progress === null) {
      return "-";
    }

    const isCompleted = service.status === "completed";
    const isInProgress = service.status === "in_progress";

    return (
      <div className="progress-wrapper">
        <div
          className={`progress-bar ${isCompleted ? "completed" : ""} ${isInProgress ? "in-progress" : ""}`}
        >
          <div
            className="progress-fill"
            style={{ width: `${service.progress}%` }}
          />
        </div>
        <span
          className={`progress-text ${isCompleted ? "completed" : ""} ${isInProgress ? "in-progress" : ""}`}
        >
          {service.progress}%
        </span>
      </div>
    );
  };

  const renderServicesTable = () => (
    <div className="tax-services-box">
      <div className="services-header">
        <h2 className="services-title">Tax Services</h2>
      </div>

      <div className="services-table">
        <div className="table-header">
          <div className="col-task">Task</div>
          <div className="col-date">Date</div>
          <div className="col-status">Status</div>
          <div className="col-type">Type</div>
          <div className="col-progress">Progress</div>
          <div className="col-action">Action</div>
        </div>

        <div className="table-body">
          {services.map((service) => (
            <div key={service.id} className="table-row">
              <div className="col-task">{service.task}</div>
              <div className="col-date">{service.date || "-"}</div>
              <div className="col-status">
                <span className={`status-badge status-${service.status}`}>
                  {getStatusLabel(service.status)}
                </span>
              </div>
              <div className="col-type">{service.type}</div>
              <div className="col-progress">{renderProgressCell(service)}</div>
              <div className="col-action">{renderActionButton(service)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderMobileTaxReturnSummary = () => {
    if (!taxReturnSummary) return null;

    const hasDownloadableFiles =
      taxReturnSummary.files?.isFederalFileReady ||
      taxReturnSummary.files?.isStateFileReady;

    return (
      <div className="mobile-tax-return-card">
        <div className="mobile-return-title-row">
          <span className="mobile-return-title">
            Your {taxReturnSummary.taxYear} Tax Return
          </span>
        </div>

        <div className="mobile-refund-row">
          <div className="mobile-refund-col">
            <span className="mobile-refund-label">
              {refundLabelFor("FEDERAL", taxReturnSummary.federalIsRefund)}
            </span>
            <span className="mobile-refund-amount">
              {formatCurrency(taxReturnSummary.federalRefund)}
            </span>
          </div>
          <div className="mobile-refund-col">
            <span className="mobile-refund-label">
              {refundLabelFor("STATE", taxReturnSummary.stateIsRefund)}
            </span>
            <span className="mobile-refund-amount">
              {formatCurrency(taxReturnSummary.stateRefund)}
            </span>
          </div>
        </div>

        {hasDownloadableFiles && (
          <button
            className="mobile-return-detail-btn"
            onClick={handleDownloadDocuments}
          >
            Your Return Detail
          </button>
        )}
      </div>
    );
  };

  const renderMobileServiceCards = () => (
    <div className="mobile-service-cards">
      {services.map((service) => {
        const isCompleted = service.status === "completed";
        const isInProgress = service.status === "in_progress";

        return (
          <div
            key={service.id}
            className={`mobile-service-card mobile-service-${service.status}`}
            onClick={() =>
              !isCompleted ? handleServiceClick(service) : undefined
            }
          >
            <div className="mobile-card-row">
              <span className="mobile-card-task">{service.task}</span>
              {isCompleted && (
                <div className="mobile-card-completed">
                  <span className="mobile-card-completed-text">Completed</span>
                  <img
                    src={CheckCircleIcon}
                    alt="Completed"
                    className="mobile-card-check-icon"
                  />
                </div>
              )}
              {isInProgress && (
                <button
                  className="mobile-card-continue-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleServiceClick(service);
                  }}
                >
                  Continue
                </button>
              )}
              {!isCompleted && !isInProgress && (
                <img
                  src={ChevronRightIcon}
                  alt="Start"
                  className="mobile-card-chevron"
                />
              )}
            </div>

            {isInProgress && service.progress !== null && (
              <div className="mobile-card-progress-section">
                <span className="mobile-card-step-label">
                  {service.progress < 100 ? "In Progress" : "Almost Done"}
                </span>
                <div className="mobile-card-progress-bar-wrapper">
                  <div className="mobile-card-progress-bar">
                    <div
                      className="mobile-card-progress-fill"
                      style={{ width: `${service.progress}%` }}
                    />
                  </div>
                  <span className="mobile-card-progress-text">
                    {service.progress}%
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={containerClass}>
      <div className="tax-wrapper">
        {currentStep === "empty" && (
          <div className="tax-header">
            <img
              src={WavvestTextLogo}
              alt="Wavvest"
              className="wavvest-text-logo"
            />
            <span className="divider">|</span>
            <img src={AprilLogo} alt="April" className="april-logo" />
          </div>
        )}

        {currentStep === "loading" && renderLoadingState()}
        {currentStep === "empty" && renderEmptyState()}
        {currentStep === "services" && (
          <>
            <div className="tax-desktop-view">
              {renderTaxReturnSummary()}
              {renderServicesTable()}
            </div>
            <div className="tax-mobile-view">
              {renderMobileTaxReturnSummary()}
              {renderMobileServiceCards()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Tax;
